import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";

const TOKEN_KEY = "buku_stok_token";
const USER_KEY = "buku_stok_user";

// When true, the app is running inside an Emergent preview environment and we
// skip the login flow entirely by pretending we're already authenticated as
// the owner. Production hosts (e.g. valcosu.co.id) still require real login.
const IS_EMERGENT_PREVIEW =
  typeof window !== "undefined" &&
  window.location &&
  window.location.hostname.toLowerCase().includes("emergent");

const PREVIEW_OWNER = {
  id: "preview-owner",
  email: "preview@emergent.local",
  name: "Preview Owner",
  role: "owner",
};

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [ready, setReady] = useState(false);
  const [user, setUser] = useState(null);
  const [rolePermissions, setRolePermissions] = useState(null);

  // Load role permissions (menu access map for admin & kasir) once auth is ready.
  useEffect(() => {
    if (!user) return;
    api
      .get("/settings/role-permissions")
      .then((r) => setRolePermissions(r.data))
      .catch(() => setRolePermissions(null));
  }, [user]);

  useEffect(() => {
    if (IS_EMERGENT_PREVIEW) {
      api
        .get("/auth/me")
        .then((r) => setUser(r.data))
        .catch(() => setUser(PREVIEW_OWNER))
        .finally(() => setReady(true));
      return;
    }

    const cachedUser = localStorage.getItem(USER_KEY);
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setReady(true);
      return;
    }
    if (cachedUser) {
      try {
        setUser(JSON.parse(cachedUser));
      } catch {
        /* ignore */
      }
    }
    api
      .get("/auth/me")
      .then((r) => {
        setUser(r.data);
        localStorage.setItem(USER_KEY, JSON.stringify(r.data));
      })
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
        setUser(null);
      })
      .finally(() => setReady(true));
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem(TOKEN_KEY, data.token);
    localStorage.setItem(USER_KEY, JSON.stringify(data.user));
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(() => {
    if (IS_EMERGENT_PREVIEW) {
      window.location.href = "/dashboard";
      return;
    }
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    sessionStorage.removeItem("buku_stok_pin_ok");
    setUser(null);
    window.location.href = "/login";
  }, []);

  // Menu access helper. Owner always has full access.
  // For admin/kasir, check rolePermissions map; if not yet loaded, allow.
  const hasMenuAccess = useCallback(
    (menuKey) => {
      if (!user) return false;
      if (user.role === "owner") return true;
      if (!menuKey) return true;
      if (!rolePermissions) return true; // permissive until loaded
      const map = rolePermissions[user.role];
      if (!map) return true;
      return map[menuKey] !== false;
    },
    [user, rolePermissions],
  );

  const reloadRolePermissions = useCallback(async () => {
    try {
      const r = await api.get("/settings/role-permissions");
      setRolePermissions(r.data);
    } catch {
      /* ignore */
    }
  }, []);

  const value = useMemo(
    () => ({
      ready,
      user,
      login,
      logout,
      isPreview: IS_EMERGENT_PREVIEW,
      rolePermissions,
      hasMenuAccess,
      reloadRolePermissions,
    }),
    [ready, user, login, logout, rolePermissions, hasMenuAccess, reloadRolePermissions],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}

export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}
